(() => {
  "use strict";

  const LONDON_TIME_ZONE = "Europe/London";
  const PPK_CUTOFF_HOUR = 14;
  const STOCKED_DELIVERY_WORKING_DAYS = 1;
  const DROPSHIP_DELIVERY_WORKING_DAYS = 2;

  // England and Wales dates from https://www.gov.uk/bank-holidays.json.
  // Keep this list isolated so the next published GOV.UK year can be added here.
  const ENGLAND_WALES_BANK_HOLIDAYS = new Set([
    "2026-01-01",
    "2026-04-03",
    "2026-04-06",
    "2026-05-04",
    "2026-05-25",
    "2026-08-31",
    "2026-12-25",
    "2026-12-28",
    "2027-01-01",
    "2027-03-26",
    "2027-03-29",
    "2027-05-03",
    "2027-05-31",
    "2027-08-30",
    "2027-12-27",
    "2027-12-28",
    "2028-01-03",
    "2028-04-14",
    "2028-04-17",
    "2028-05-01",
    "2028-05-29",
    "2028-08-28",
    "2028-12-25",
    "2028-12-26"
  ]);

  const londonDateTimeFormatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: LONDON_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23"
  });

  const displayDateFormatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: LONDON_TIME_ZONE,
    weekday: "long",
    day: "numeric",
    month: "long"
  });

  function londonDateTime(date) {
    const values = {};

    londonDateTimeFormatter.formatToParts(date).forEach((part) => {
      if (part.type !== "literal") {
        values[part.type] = Number(part.value);
      }
    });

    return {
      year: values.year,
      month: values.month,
      day: values.day,
      hour: values.hour,
      minute: values.minute
    };
  }

  function dateOnly(value) {
    return { year: value.year, month: value.month, day: value.day };
  }

  function dateKey(value) {
    return [value.year, String(value.month).padStart(2, "0"), String(value.day).padStart(2, "0")].join("-");
  }

  function addCalendarDays(value, days) {
    const date = new Date(Date.UTC(value.year, value.month - 1, value.day + days, 12));

    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate()
    };
  }

  function isWorkingDay(value) {
    const weekday = new Date(Date.UTC(value.year, value.month - 1, value.day, 12)).getUTCDay();
    return weekday !== 0 && weekday !== 6 && !ENGLAND_WALES_BANK_HOLIDAYS.has(dateKey(value));
  }

  function nextWorkingDay(value) {
    let candidate = addCalendarDays(value, 1);

    while (!isWorkingDay(candidate)) {
      candidate = addCalendarDays(candidate, 1);
    }

    return candidate;
  }

  function addWorkingDays(value, workingDays) {
    let candidate = value;
    let remaining = workingDays;

    while (remaining > 0) {
      candidate = addCalendarDays(candidate, 1);

      if (isWorkingDay(candidate)) {
        remaining -= 1;
      }
    }

    return candidate;
  }

  function sameDate(first, second) {
    return dateKey(first) === dateKey(second);
  }

  function formatDisplayDate(value) {
    return displayDateFormatter.format(new Date(Date.UTC(value.year, value.month - 1, value.day, 12)));
  }

  function deliveryIsTomorrow(deliveryDate, now) {
    const today = dateOnly(londonDateTime(now));
    return sameDate(deliveryDate, addCalendarDays(today, 1));
  }

  function stockedEstimate(now) {
    const londonNow = londonDateTime(now);
    const today = dateOnly(londonNow);
    const beforeCutoff = londonNow.hour < PPK_CUTOFF_HOUR;
    const dispatchDate = isWorkingDay(today) && beforeCutoff ? today : nextWorkingDay(today);

    return {
      dispatchDate,
      dispatchToday: sameDate(today, dispatchDate),
      deliveryDate: addWorkingDays(dispatchDate, STOCKED_DELIVERY_WORKING_DAYS)
    };
  }

  function dropshipEstimate(now) {
    const today = dateOnly(londonDateTime(now));

    return {
      deliveryDate: addWorkingDays(today, DROPSHIP_DELIVERY_WORKING_DAYS)
    };
  }

  function fulfilmentState(variant) {
    return ["stocked", "dropship", "delivery-stock", "unavailable", "unknown"].includes(variant?.state)
      ? variant.state : "unknown";
  }

  const testApi = {
    addWorkingDays,
    dateKey,
    deliveryIsTomorrow,
    dropshipEstimate,
    fulfilmentState,
    isWorkingDay,
    londonDateTime,
    nextWorkingDay,
    stockedEstimate
  };

  if (typeof module !== "undefined" && module.exports) {
    module.exports = testApi;
  }

  if (typeof window === "undefined" || !window.customElements) {
    return;
  }

  class PumpAdsDelivery extends HTMLElement {
    connectedCallback() {
      if (this._connected) {
        return;
      }

      this._connected = true;
      this._variants = this._readVariants();
      this._variantId = String(this.dataset.selectedVariantId || "");
      this._form = this.closest("form");
      this._onVariantChanged = this._handleVariantChanged.bind(this);
      this._form?.addEventListener("variant:changed", this._onVariantChanged);
      this._refreshTimer = window.setInterval(() => this._render(), 60000);
      this._render();
    }

    disconnectedCallback() {
      this._form?.removeEventListener("variant:changed", this._onVariantChanged);
      window.clearInterval(this._refreshTimer);
      this._connected = false;
    }

    _readVariants() {
      const source = this.querySelector("[data-pump-ads-variants]");

      try {
        return JSON.parse(source?.textContent || "{}");
      } catch (_error) {
        return {};
      }
    }

    _handleVariantChanged(event) {
      if (event.detail?.variant?.id) {
        this._variantId = String(event.detail.variant.id);
        this._render();
      }
    }

    _render() {
      const variant = this._variants[this._variantId];
      const state = fulfilmentState(variant);
      const status = this.querySelector("[data-pump-ads-status]");
      const dispatch = this.querySelector("[data-pump-ads-dispatch]");
      const estimate = this.querySelector("[data-pump-ads-estimate]");

      this.dataset.state = state;
      const freeDelivery = this.querySelector("[data-pump-ads-free-delivery]");
      if (freeDelivery) freeDelivery.hidden = !variant?.standardService;

      if (!status || !dispatch || !estimate) {
        return;
      }

      dispatch.hidden = true;
      estimate.hidden = true;
      dispatch.textContent = "";
      estimate.textContent = "";
      estimate.dataset.deliveryTomorrow = "false";
      const now = new Date();

      const statusMessages = {
        stocked: "In stock at Perfect Ponds & Koi",
        dropship: "In stock",
        unavailable: "Currently unavailable",
        "delivery-stock": "In stock",
        unknown: "Available to order"
      };
      status.textContent = statusMessages[state];
      if (state === "unavailable") return;
      if (!variant?.standardService || !variant?.workingDays) {
        dispatch.textContent = "Contact us to confirm the delivery date for this pump.";
        dispatch.hidden = false;
        return;
      }

      if (state === "stocked") {
        const dates = stockedEstimate(now);
        const isTomorrow = deliveryIsTomorrow(dates.deliveryDate, now);
        status.textContent = "In stock at Perfect Ponds & Koi";
        dispatch.textContent = dates.dispatchToday
          ? "Order now and it can be dispatched today."
          : `Order now for dispatch on ${formatDisplayDate(dates.dispatchDate)}.`;
        const deliveryLabel = document.createElement("span");
        deliveryLabel.className = "pump-ads-reassurance__delivery-highlight";
        deliveryLabel.textContent = isTomorrow ? "Tomorrow" : "1 working day after dispatch";
        if (!isTomorrow) {
          deliveryLabel.title = "1 working day after dispatch";
        }
        estimate.append("Estimated delivery: ", deliveryLabel, ` - ${formatDisplayDate(dates.deliveryDate)}.`);
        estimate.dataset.deliveryTomorrow = String(isTomorrow);
        dispatch.hidden = false;
        estimate.hidden = false;
        return;
      }

      if (state === "dropship" || state === "delivery-stock") {
        const dates = dropshipEstimate(now);
        status.textContent = "In stock";
        dispatch.textContent = "Estimated delivery in 2 working days.";
        estimate.textContent = `Estimated delivery date: ${formatDisplayDate(dates.deliveryDate)}.`;
        dispatch.hidden = false;
        estimate.hidden = false;
        return;
      }

      status.textContent = "Currently unavailable";
    }
  }

  if (!window.customElements.get("pump-ads-delivery")) {
    window.customElements.define("pump-ads-delivery", PumpAdsDelivery);
  }
})();
